import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Chip,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Assessment as AnalyticsIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import ordersService, {
  Order,
  OrdersFilters,
  CreateOrderPayload,
} from "../services/ordersService";

interface CityOption {
  name: string;
}

const OrderManagement: React.FC = () => {
  // State Management
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [filterRiderId, setFilterRiderId] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [analyticsTotalOrders, setAnalyticsTotalOrders] = useState<number | null>(null);
  const [formError, setFormError] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  // Form State
  const [formData, setFormData] = useState<CreateOrderPayload>({
    riderPublicRiderId: "",
    clientId: "",
    clientName: "",
    city: "",
    date: new Date().toISOString().split("T")[0],
    totalOrdersDay: 0,
  });

  // Fetch all orders with filters
  const fetchOrders = async (filters?: OrdersFilters) => {
    setLoading(true);
    try {
      const response = await ordersService.getOrders({
        page: filters?.page || page,
        pageSize: filters?.pageSize || pageSize,
        riderId: filterRiderId || undefined,
        city: selectedCity || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });

      setOrders(response.data);
      if (response.pagination) {
        setPage(response.pagination.page);
        setPageSize(response.pagination.pageSize);
        setTotalPages(response.pagination.totalPages);
        setTotalRecords(response.pagination.total);
      }
    } catch (error: any) {
      enqueueSnackbar(
        error.message || "Failed to fetch orders",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch analytics total (sum of city summary totalOrders)
  const fetchAnalyticsTotal = async () => {
    try {
      const resp = await ordersService.getCitySummary({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined });
      const summary = resp?.summary || [];
      const sum = summary.reduce((s: number, c: any) => s + (c.totalOrders || 0), 0);
      setAnalyticsTotalOrders(sum);
    } catch (err: any) {
      console.error('Failed to fetch analytics total orders:', err);
      setAnalyticsTotalOrders(null);
    }
  };

  // Fetch cities (mock - replace with actual API call)
  const fetchCities = async () => {
    try {
      // This should call your actual cities API
      const mockCities: CityOption[] = [
        { name: "Chennai" },
        { name: "Bengaluru" },
        { name: "Hyderabad" },
        { name: "Delhi" },
        { name: "Mumbai" },
        { name: "Mysore" }
      ];
      setCities(mockCities); 
    } catch (error: any) {
      console.error("Failed to fetch cities:", error);
    }
  };

  // Initialize data on mount
  useEffect(() => {
    fetchOrders();
    fetchCities();
    fetchAnalyticsTotal();
  }, []);

  // Fetch orders when filters change
  useEffect(() => {
    if (page > 0 && pageSize > 0) {
      fetchOrders();
    }
    // refresh analytics total whenever filters change
    fetchAnalyticsTotal();
  }, [filterRiderId, selectedCity, dateFrom, dateTo]);

  // Handle form input changes
  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | any>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "totalOrdersDay" ? (value ? parseInt(value) : 0) : value,
    }));
  };

  // Handle create/update order
  const handleSaveOrder = async () => {
    setFormError("");
    if (
      !formData.riderPublicRiderId ||
      !formData.city ||
      !formData.date ||
      formData.totalOrdersDay < 0
    ) {
      enqueueSnackbar("Please fill in all required fields", {
        variant: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      if (editingOrder) {
        // Update existing order
        await ordersService.updateOrder(editingOrder.id, {
          ...formData,
        } as any);
        enqueueSnackbar("Order updated successfully", { variant: "success" });
      } else {
        // Create new order
        await ordersService.createOrder(formData);
        enqueueSnackbar("Order created successfully", { variant: "success" });
      }

      // Reset form and refresh data
      setOpenDialog(false);
      setEditingOrder(null);
      setFormData({
        riderPublicRiderId: "",
        clientId: "",
        clientName: "",
        city: "",
        date: new Date().toISOString().split("T")[0],
        totalOrdersDay: 0,
      });

      // Reset filters and page to show newly created order
      setFilterRiderId("");
      setSelectedCity("");
      setDateFrom("");
      setDateTo("");
      setPage(1);

      // Fetch all orders without filters to show the newly created one
      const response = await ordersService.getOrders({
        page: 1,
        pageSize: 20,
      });
      setOrders(response.data);
      if (response.pagination) {
        setPage(response.pagination.page);
        setPageSize(response.pagination.pageSize);
        setTotalPages(response.pagination.totalPages);
        setTotalRecords(response.pagination.total);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to save order";
      setFormError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Handle edit order
  const handleEditOrder = (order: Order) => {
    setEditingOrder(order);
    setFormData({
      riderPublicRiderId: order.riderPublicRiderId,
      clientId: order.clientId || "",
      clientName: order.clientName || "",
      city: order.city || "",
      date: order.date ? order.date.split("T")[0] : "",
      totalOrdersDay: order.totalOrdersDay,
    });
    setOpenDialog(true);
  };

  // Handle delete order
  const handleDeleteOrder = async (orderId: string) => {
    if (!window.confirm("Are you sure you want to delete this order?")) {
      return;
    }

    setLoading(true);
    try {
      await ordersService.deleteOrder(orderId);
      enqueueSnackbar("Order deleted successfully", { variant: "success" });
      await fetchOrders();
    } catch (error: any) {
      enqueueSnackbar(
        error.message || "Failed to delete order",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle open dialog
  const handleOpenDialog = () => {
    setEditingOrder(null);
    setFormData({
      riderPublicRiderId: "",
      clientId: "",
      clientName: "",
      city: "",
      date: new Date().toISOString().split("T")[0],
      totalOrdersDay: 0,
    });
    setOpenDialog(true);
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOrder(null);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setFilterRiderId("");
    setSelectedCity("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
    fetchOrders({
      page: 1,
      pageSize,
      riderId: undefined,
      city: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    });
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Order Management
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<AnalyticsIcon />}
              onClick={() => navigate('/order-analytics')}
              disabled={loading}
            >
              Analytics
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => fetchOrders()}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => navigate('/orders/bulk-upload')}
              disabled={loading}
            >
              Upload
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              disabled={loading}
            >
              Create Order
            </Button>
          </Box>
        </Box>
        <Divider />
      </Box>

      {/* Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Rider ID"
                placeholder="e.g., MAA-25-R000001"
                value={filterRiderId}
                onChange={(e) => {
                  setFilterRiderId(e.target.value);
                  setPage(1);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  value={selectedCity}
                  onChange={(e) => {
                    setSelectedCity(e.target.value);
                    setPage(1);
                  }}
                  label="City"
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city.name} value={city.name}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Date From"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Date To"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                sx={{ mt: 1 }}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardHeader
          title={`Orders (${(analyticsTotalOrders ?? totalRecords).toLocaleString()} Total)`}
          subheader={`Page ${page} of ${totalPages}`}
        />
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : orders.length === 0 ? (
            <Alert severity="info">No orders found</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Rider ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>ClientRider ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Client Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Orders
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Chip
                          label={order.riderPublicRiderId}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{order.clientId || "N/A"}</TableCell>
                      <TableCell>{order.clientName || "N/A"}</TableCell>
                      <TableCell>{order.city || "N/A"}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={order.totalOrdersDay}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(order.date)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditOrder(order)}
                          disabled={loading}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteOrder(order.id)}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 3 }}>
              <Button
                disabled={page === 1}
                onClick={() => {
                  setPage(page - 1);
                  fetchOrders({ page: page - 1, pageSize });
                }}
              >
                Previous
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
                Page {page} of {totalPages}
              </Box>
              <Button
                disabled={page === totalPages}
                onClick={() => {
                  setPage(page + 1);
                  fetchOrders({ page: page + 1, pageSize });
                }}
              >
                Next
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Order Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOrder ? "Edit Order" : "Create New Order"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Rider ID"
              placeholder="e.g., MAA-25-R000001"
              name="riderPublicRiderId"
              value={formData.riderPublicRiderId}
              onChange={handleFormChange}
              required
              helperText="Enter a valid rider ID. Backend will validate if the rider exists."
            />

            <TextField
              fullWidth
              label="Client Name"
              name="clientName"
              value={formData.clientName}
              onChange={handleFormChange}
            />

            <TextField
              fullWidth
              label="Client ID"
              name="clientId"
              value={formData.clientId}
              onChange={handleFormChange}
            />

            <FormControl fullWidth required>
              <InputLabel>City</InputLabel>
              <Select
                name="city"
                value={formData.city}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }));
                }}
                label="City"
              >
                <MenuItem value="">Select a City</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city.name} value={city.name}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="date"
              label="Date"
              name="date"
              value={formData.date}
              onChange={handleFormChange}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              type="number"
              label="Total Orders"
              name="totalOrdersDay"
              value={formData.totalOrdersDay}
              onChange={handleFormChange}
              inputProps={{ min: 0 }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveOrder}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Save Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderManagement;
